import * as Model from "@effect/sql/Model";
import { Schema } from "effect";

/**
 * Quality profile schema for persona evaluation dimensions.
 * @since 1.0.0
 */
export const QualityProfileSchema = Schema.Struct({
  seduction: Schema.Number,
  surrender: Schema.Number,
  depth: Schema.Number,
  imagination: Schema.Number,
});

export type QualityProfile = Schema.Schema.Type<typeof QualityProfileSchema>;

/**
 * Shadow profile entry schema.
 * @since 1.0.0
 */
export const ShadowProfileEntrySchema = Schema.Struct({
  quality: Schema.String,
  type: Schema.Literal("excess", "deficit"),
  description: Schema.String,
});

export type ShadowProfileEntry = Schema.Schema.Type<typeof ShadowProfileEntrySchema>;

/**
 * Persona status enum.
 * - draft: Persona is being built through LLM conversation
 * - published: Persona is complete and available for use
 * - archived: Persona is no longer active
 * @since 1.0.0
 */
export const PersonaStatus = Schema.Literal("draft", "published", "archived");
export type PersonaStatus = Schema.Schema.Type<typeof PersonaStatus>;

/**
 * Persona model for persona builder.
 *
 * Stores persona configuration including psychological profile,
 * communication markers, and shadow profile.
 *
 * @since 1.0.0
 */
export class Persona extends Model.Class<Persona>("Persona")({
  id: Model.Generated(Schema.String),
  personId: Schema.NullOr(Schema.String),
  conversationId: Schema.NullOr(Schema.String),
  name: Schema.NullOr(Schema.String),
  age: Schema.NullOr(Schema.Number),
  occupation: Schema.NullOr(Schema.String),
  familyOfOrigin: Schema.NullOr(Schema.String),
  formativeExperiences: Schema.NullOr(Schema.String),
  psychologicalCore: Schema.NullOr(Schema.String),
  expressionInConversation: Schema.NullOr(Schema.String),
  shadowTriggers: Schema.NullOr(Schema.String),
  qualityProfile: Schema.NullOr(QualityProfileSchema),
  communicationMarkers: Schema.Array(Schema.String),
  characteristicPhrases: Schema.Array(Schema.String),
  shadowProfile: Schema.Array(ShadowProfileEntrySchema),
  sourceInspiration: Schema.NullOr(Schema.String),
  status: Schema.optionalWith(PersonaStatus, { default: () => "draft" as const }),
  currentStep: Schema.optionalWith(Schema.Number, { default: () => 0 }),
  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
}) {}
